/**
 * ResultRow — one `project_chat_message_results` row inside a chat turn.
 *
 * Ported from D9's `UnifiedChatPanel` with its two non-cosmetic behaviours intact. Both are
 * load-bearing and are repeated here because they are the things most likely to be "simplified"
 * away by a future change:
 *
 *  1. 🚨 **There is no "Accept All", and there must never be one.** SEC-7 was closed by risk
 *     acceptance on the explicit basis that a human reviews every proposal before it reaches
 *     `artifact_edits.content`. Per-proposal accept is therefore a SECURITY control, not a
 *     convenience; a bulk affordance voids the acceptance (CR §8 OQ-4, Track 3 §5.3.1).
 *  2. **Reject is a server call, not a local dismiss.** The decision is recorded in
 *     `project_chat_message_results.resolution`. Removing the card client-side would lose the
 *     record and leave the proposal permanently open server-side (Track 3 §5.3.2).
 *
 * Accept routes through `useResolveProjectChatResult` (PATCH /chat), never `usePatchArtifact` — the
 * legacy path skips the content-hash staleness check and never records the resolution.
 *
 * `onResolved` exists for focus management: resolving a proposal removes this row from the DOM, so
 * the parent moves focus to the composer rather than letting it fall to `<body>` (Track 3 T3-4).
 */

import { GitBranch, Lock } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ARTIFACT_LABELS, type ArtifactType } from "@/hooks/useArtifacts";
import {
  useResolveProjectChatResult,
  type ProjectChatResult,
} from "@/hooks/useProjectChat";
import { DiffProposal } from "@/components/projects/artifacts/DiffProposal";
import { toastIfNotProjectMember } from "@/lib/project-permissions";
import { describeSkip } from "./skipNotice";

export interface ResultRowProps {
  projectId: string;
  result: ProjectChatResult;
  /** Called after an accept or reject settles, so the parent can restore focus. */
  onResolved?: () => void;
  /**
   * Opens a file tab. Supplied by `Phase0Page` (`handleOpenArtifact`). When absent — the docked
   * variant, which is already inside a file — the skip notice drops the button and keeps the
   * instruction in prose, so the explanation never depends on the button being available.
   */
  onOpenArtifact?: (artifactType: ArtifactType) => void;
  /**
   * Whether the viewer may accept / reject the proposal (project member, or leadership/admin).
   * False keeps the diff readable but hides the controls — the server answers 403
   * NOT_PROJECT_MEMBER otherwise. Defaults to true; `Phase0Page` passes the real gate.
   */
  canResolve?: boolean;
}

export function ResultRow({
  projectId,
  result,
  onResolved,
  onOpenArtifact,
  canResolve = true,
}: ResultRowProps): JSX.Element {
  const { mutateAsync: resolve, isPending } =
    useResolveProjectChatResult(projectId);
  const label = ARTIFACT_LABELS[result.artifact_type] ?? result.artifact_type;

  if (result.resolution !== "proposed") {
    // A skip is a REFUSAL and has to read like one. Before this, every non-proposal resolution
    // rendered as the same grey "Label — Skipped" line, which is indistinguishable from the app
    // having done nothing — and when every tagged file is locked there is no agent turn to
    // contradict that impression. See `skipNotice.ts` for the copy rules.
    const notice = describeSkip(label, result.resolution, result.reason);
    const isWarning = notice.tone === "warning";
    const showOpenButton =
      notice.offersOpenFile && onOpenArtifact !== undefined;

    return (
      <div
        className={cn(
          // `border-outlines` named explicitly (§9.3b). The tone branches below that set their own
          // border colour continue to override this.
          "mt-2 rounded-md border border-outlines p-3 text-sm",
          isWarning
            ? "border-warning/40 bg-badge-yellow-bg"
            : "border-outlines bg-subtle-bg",
        )}
      >
        <div className="flex items-start gap-2">
          {isWarning && (
            <Lock
              className="mt-0.5 h-4 w-4 shrink-0 text-warning"
              aria-hidden="true"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-text-primary">{notice.title}</p>

            {notice.explanation && (
              <p className="mt-1 text-text-secondary">{notice.explanation}</p>
            )}

            {notice.nextStep && (
              <p className="mt-1.5 text-text-primary">
                <span className="font-medium">What to do: </span>
                {notice.nextStep}
              </p>
            )}

            {/* The server's own words, kept as secondary detail so support can match a screenshot
                to a log line and so an unrecognised reason is never swallowed. */}
            {notice.serverReason && (
              <p className="mt-1.5 text-xs text-text-secondary">
                {notice.serverReason}
              </p>
            )}

            {showOpenButton && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => onOpenArtifact?.(result.artifact_type)}
              >
                Open {label}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Item 4 (§5.4c) — committed-artifact disclosure ──────────────────────────────────────────
  //
  // `starts_new_version` is true when the tagged artifact is CURRENTLY `committed`, so accepting
  // this proposal moves a signed-off, GitHub-committed document back to `draft_edit`. Removing the
  // chat lock without saying so was the thing §5.4c explicitly refused to ship: "a one-line chat
  // instruction [would] move a signed-off document to draft_edit with no notice."
  //
  // The disclosure lives on the PROPOSAL, not on the result, because `acceptResult` is the write
  // point — telling the user afterwards would be an apology, not a choice. OQ-4's accepted risk
  // rests on the per-proposal human accept being a real control, and a control the user cannot
  // see the consequences of is not one.
  //
  // The field is absent on an old payload (pre-Item-4 backend, or a resolved row), and `undefined`
  // is correctly falsy — so this degrades to exactly today's rendering rather than crashing or
  // showing a notice it cannot justify.
  const startsNewVersion = result.starts_new_version === true;

  return (
    <DiffProposal
      projectId={projectId}
      artifactType={result.artifact_type}
      headerSuffix={label}
      readOnly={!canResolve}
      proposedContent={result.proposed_content ?? ""}
      diff={result.diff ?? ""}
      agentNote={result.agent_note ?? ""}
      discardLabel={isPending ? "Rejecting…" : "Reject"}
      acceptLabel={startsNewVersion ? "Accept — start new version" : undefined}
      notice={
        startsNewVersion ? (
          <div
            className="mt-2 flex items-start gap-2 rounded-md border border-warning/40 bg-badge-yellow-bg p-3 text-sm"
            role="note"
          >
            <GitBranch
              className="mt-0.5 h-4 w-4 shrink-0 text-warning"
              aria-hidden="true"
            />
            <div>
              <p className="font-medium text-text-primary">
                {`Accepting this starts a new version of ${label}.`}
              </p>
              <p className="mt-1 text-text-secondary">
                The committed version stays in the project&apos;s history. The
                new version needs its own approval.
              </p>
            </div>
          </div>
        ) : undefined
      }
      onAccept={async () => {
        const outcome = await resolve({
          resultId: result.result_id,
          action: "accept",
        }).catch((err: unknown) => {
          // A 409 is an expected outcome, not a crash: the file moved (stale) or is mid-commit
          // (locked). Surfacing the server's message is the whole point — a silent failure here is
          // indistinguishable from a lost update.
          if (toastIfNotProjectMember(err)) throw err;
          const message =
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? "Could not accept this change.";
          toast.error(message);
          throw err;
        });
        if (outcome.applied) toast.success(`${label} updated`);
      }}
      onAccepted={() => {
        // The transcript refetch is the source of truth for content; this callback only restores
        // focus, because the row is about to unmount.
        onResolved?.();
      }}
      onDiscarded={() => {
        void resolve({ resultId: result.result_id, action: "reject" })
          .then(() => {
            toast.success(`${label} proposal rejected`);
            onResolved?.();
          })
          .catch((err: unknown) => {
            if (!toastIfNotProjectMember(err))
              toast.error("Could not reject this proposal.");
          });
      }}
    />
  );
}

export default ResultRow;
